
import Auth from '../../api/auth/Auth';
import { CustomerFilter } from '../../api/customers/customer_filter';
import { FilterPersistence } from '../../api/filter/filter_persistence';
import { formatDate, parseDate } from '../../api/dates/date_formatter';
import { RateFilter } from '../../api/rates/rate_filter';
import {
  TeeTimeIndex,
  TeeTimeIndexFilters,
  TeeTimeIndexOptions,
} from '../../api/tee_times/index';
import { TeeTime } from '../../api/tee_times/tee_time';
import { CourseList } from '../../api/courses/list';
import { Reporting } from '../../api/reports/reporting';

import Vue from 'vue';

export default Vue.component('tee-time-index', {
  data() {
    return {
      agentFilter: '',
      customerFilter: {} as CustomerFilter,
      selectedCreatedAtDates: [] as string[],
      createdAtFilterDisplay: '' as string,
      createdAtMenu: false,
      selectedPlayDates: [] as string[],
      playDateFilterDisplay: '' as string,
      playDateMenu: false,
      playTimeFilter: '',
      playersFilter: '',
      courseFilter: [] as string[],
      availableCourses: [] as { text: string; value: string }[],
      notesFilter: '',
      statusFilter: '',
      internalNotesFilter: '',
      rateFilter: {} as RateFilter,
      confirmationFilter: '',
      fieldFilters: {} as TeeTimeIndexFilters,
      picker: new Date().toISOString().substr(0, 10),
      createdAt: '' as string,
      loading: true,
      loadingText: 'Loading Data...',
      headers: [
        { text: 'View', value: 'view' },
        {
          text: 'First Name',
          value: 'customer.first_name',
        },
        {
          text: 'Last Name',
          value: 'customer.last_name',
        },
        {
          text: 'Status',
          value: 'status',
        },
        {
          text: 'Created At Date',
          value: 'created_at_date',
        },
        {
          text: 'Created At Time',
          value: 'created_at_time',
        },
        {
          text: 'Play Date',
          value: 'play_date',
        },
        { text: 'Play Time', value: 'play_time' },
        { text: 'Players', value: 'players' },
        { text: 'Paid', value: 'transaction_record.total' },
        { text: 'Cost', value: 'cost' },
        { text: 'Course', value: 'course_name' },
        { text: 'Confirmation', value: 'confirmation' },
        { text: 'CC Conf', value: 'transaction_record.payment_id' },
        { text: 'Agent', value: 'agent' },
        { text: 'Notes', value: 'notes' },
        { text: 'Internal Notes', value: 'internal_notes' },
      ],
      options: {} as TeeTimeIndexOptions,
      teeTimeData: [] as TeeTime[],
      teeTimeCount: 0 as number,
      perPage: TeeTimeIndex.PER_PAGE as number,
      pageNo: 1 as number,
      sortDesc: [true, false] as boolean[],
      sortBy: ['created_at_date', 'created_at_time'] as string[],
    };
  },
  mounted() {
    this.loadFilters();
    this.loadAvailableCourses();
    this.refreshTable();
  },
  computed: {
    teeTimes(): TeeTime[] {
      return this.teeTimeData;
    },
    startDate(): string {
      if (this.selectedPlayDates.length > 0) {
        return this.selectedPlayDates[0];
      }
      return '';
    },
    endDate(): string {
      if (this.selectedPlayDates.length > 1) {
        return this.selectedPlayDates[1];
      }
      return this.selectedPlayDates[0] || '';
    },
    rangeType(): string {
      return this.getQueryType() === '' ? 'played' : 'created';
    },
  },
  methods: {
    isAdmin() {
      return Auth.isAdmin();
    },
    parseDateInternal(date: string): string {
      return parseDate(date);
    },
    refreshTable() {
      this.options.sortDesc = this.sortDesc;
      this.options.sortBy = this.sortBy;
      this.options.page = this.pageNo;
      this.options.itemsPerPage = this.perPage;
      this.options.queryType = this.getQueryType();
      this.options.filters = this.fieldFilters;
      TeeTimeIndex.getData(this.options).then((response) => {
        this.teeTimeData = TeeTimeIndex.parseResponse(response.data);
        this.loading = false;
      });
      TeeTimeIndex.getCount(this.options).then((response) => {
        this.teeTimeCount = response.data;
      });
    },
    getQueryType(): string {
      if (this.$route.query.monthly === undefined) {
        return '';
      }
      if (this.$route.query.monthly === 'true') {
        return 'monthly';
      }
      return 'daily';
    },
    maybeSearch(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        if (!(event.target as HTMLElement).closest('.v-menu')) {
          this.updateFilters();
          FilterPersistence.saveFieldFilterState(this.$router, 'manage-tee-times', this.fieldFilters);
          this.search();
        }
      }
    },
    loadFilters() {
      const filter = FilterPersistence.getFieldFiltersFromUrl(this.$route);
      if (!filter) {
        return;
      }
      this.fieldFilters = filter;

      this.customerFilter = this.fieldFilters.customer;
      this.agentFilter = this.fieldFilters.agent;
      
      const createdStart = this.fieldFilters.created_at_start || '';
      const createdEnd = this.fieldFilters.created_at_end || '';
      if (createdStart && createdEnd) {
        this.selectedCreatedAtDates = [createdStart, createdEnd].sort();
      } else if (createdStart) {
        this.selectedCreatedAtDates = [createdStart];
      } else {
        this.selectedCreatedAtDates = [];
      }
      this.updateCreatedAtDisplay();

      const playStart = this.fieldFilters.play_date_start || '';
      const playEnd = this.fieldFilters.play_date_end || '';
      if (playStart && playEnd) {
        this.selectedPlayDates = [playStart, playEnd].sort();
      } else if (playStart) {
        this.selectedPlayDates = [playStart];
      } else {
        this.selectedPlayDates = [];
      }
      this.updatePlayDateDisplay();

      this.playTimeFilter = this.fieldFilters.play_time;
      this.playersFilter = this.fieldFilters.players;
      this.courseFilter = Array.isArray(this.fieldFilters.course_name) 
                          ? this.fieldFilters.course_name 
                          : (this.fieldFilters.course_name ? [this.fieldFilters.course_name] : []);
      this.confirmationFilter = this.fieldFilters.confirmation;
      this.statusFilter = this.fieldFilters.status;
      this.rateFilter = this.fieldFilters.rate;
    },
    search() {
      this.pageNo = 1;
      this.updateFilters();
      this.refreshTable();
    },
    updateFilters() {
      // TODO - Convert this to an interface so it's easier to read elsewhere.
     if (this.selectedPlayDates.length === 2) {
        this.fieldFilters.play_date_start = this.selectedPlayDates[0];
        this.fieldFilters.play_date_end = this.selectedPlayDates[1];
      } else if (this.selectedPlayDates.length === 1) {
        this.fieldFilters.play_date_start = this.selectedPlayDates[0];
        this.fieldFilters.play_date_end = this.selectedPlayDates[0];
      } else {
        this.fieldFilters.play_date_start = '';
        this.fieldFilters.play_date_end = '';
      }

      if (this.selectedCreatedAtDates.length === 2) {
        this.fieldFilters.created_at_start = this.selectedCreatedAtDates[0];
        this.fieldFilters.created_at_end = this.selectedCreatedAtDates[1];
      } else if (this.selectedCreatedAtDates.length === 1) {
        this.fieldFilters.created_at_start = this.selectedCreatedAtDates[0];
        this.fieldFilters.created_at_end = this.selectedCreatedAtDates[0];
      } else {
        this.fieldFilters.created_at_start = '';
        this.fieldFilters.created_at_end = '';
      }

      this.fieldFilters = {
        ...this.fieldFilters,
        agent: this.agentFilter,
        play_time: this.playTimeFilter,
        players: this.playersFilter,
        course_name: this.courseFilter,
        confirmation: this.confirmationFilter,
        notes: this.notesFilter,
        status: this.statusFilter,
        rate: this.rateFilter,
        customer: this.customerFilter,
        internal_notes: this.internalNotesFilter
      };
    },
    handleCreatedAtInput(dates: string | string[]) {
      if (Array.isArray(dates)) {
        this.selectedCreatedAtDates = dates.sort();
      } else if (typeof dates === 'string') {
        this.selectedCreatedAtDates = [dates];
      } else {
        this.selectedCreatedAtDates = [];
      }
      this.updateCreatedAtDisplay();
    },
    handleCreatedAtSearchAndClose() {
      this.createdAtMenu = false;
      this.updateCreatedAtDisplay();
      this.search();
    },
    clearCreatedAt() {
      this.selectedCreatedAtDates = [];
      this.updateCreatedAtDisplay();
    },
    updateCreatedAtDisplay() {
      if (this.selectedCreatedAtDates.length === 2) {
        const startFormatted = formatDate(this.selectedCreatedAtDates[0]);
        const endFormatted = formatDate(this.selectedCreatedAtDates[1]);
        this.createdAtFilterDisplay = startFormatted === endFormatted
          ? startFormatted
          : `${startFormatted} - ${endFormatted}`;
      } else if (this.selectedCreatedAtDates.length === 1) {
        this.createdAtFilterDisplay = formatDate(this.selectedCreatedAtDates[0]);
      } else {
        this.createdAtFilterDisplay = '';
      }
    },
    handlePlayDateInput(dates: string | string[]) {
      if (Array.isArray(dates)) {
        this.selectedPlayDates = dates.sort(); 
      } else if (typeof dates === 'string') {
        this.selectedPlayDates = [dates];
      } else {
        this.selectedPlayDates = [];
      }
      this.updatePlayDateDisplay();
    },
    handlePlayDateSearchAndClose() {
      this.playDateMenu = false;
      this.updatePlayDateDisplay();
      this.search();
    },
    clearPlayDate() {
      this.selectedPlayDates = [];
      this.updatePlayDateDisplay();
    },
    updatePlayDateDisplay() {
      if (this.selectedPlayDates.length === 2) {
        const startFormatted = formatDate(this.selectedPlayDates[0]);
        const endFormatted = formatDate(this.selectedPlayDates[1]);
        if (startFormatted === endFormatted) {
          this.playDateFilterDisplay = startFormatted;
        } else {
          this.playDateFilterDisplay = `${startFormatted} - ${endFormatted}`;
        }
      } else if (this.selectedPlayDates.length === 1) {
        this.playDateFilterDisplay = formatDate(this.selectedPlayDates[0]);
      } else {
        this.playDateFilterDisplay = '';
      }
    },
    loadAvailableCourses() {
      CourseList.get().then(response => {
        this.availableCourses = response.data.map((courseItem: any) => ({
          text: courseItem[1],
          value: courseItem[1],
        }));
      }).catch(error => {
        console.error("Error loading courses for TeeTimeIndex:", error);
      });
    },
    exportToExcel() {
      const noPlayDateRange =
        !this.fieldFilters.play_date_start || !this.fieldFilters.play_date_end;

      const noCreatedAtRange =
        !this.fieldFilters.created_at_start || !this.fieldFilters.created_at_end;

      if (noPlayDateRange && noCreatedAtRange) {
        alert('Please select a Play Date or Created At date range before exporting');
        return;
      }

      Reporting.getTeeTimesExportReport(this.fieldFilters);
    },
  },
  watch: {
    pageNo: {
      handler() {
        this.refreshTable();
      },
    },
    perPage: {
      handler() {
        this.refreshTable();
      },
    },
    sortDesc: {
      handler() {
        this.refreshTable();
      },
    },
  }
});
